/* eslint-disable react-refresh/only-export-components */
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { RootLayout } from './layouts/RootLayout'
import { AuthGuard } from './guards/AuthGuard'
import { AdminGuard } from './guards/AdminGuard'
import { DashboardLayout } from '@/components/navigation/DashboardLayout'
import { NavigationProvider } from '@/components/providers/NavigationProvider'
import { OnboardingGuard } from '@/components/OnboardingGuard'

// Layout wrapper for routes that need the sidebar, navigation context, and onboarding check
const DashboardLayoutWrapper = () => (
  <OnboardingGuard>
    <NavigationProvider>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </NavigationProvider>
  </OnboardingGuard>
)

// Loading component
const PageLoading = () => (
  <div className="flex items-center justify-center min-h-screen bg-white dark:bg-[#020617]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
)

// Legal pages
const LegalLayout = lazy(() => import('@/pages/legal/LegalLayout'))
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'))

// Lazy load pages for code splitting
const SigninPage = lazy(() => import('@/pages/auth/SigninPage'))
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'))
const EmailConfirmedPage = lazy(() => import('@/pages/auth/EmailConfirmedPage'))
const AuthCodeErrorPage = lazy(() => import('@/pages/auth/AuthCodeErrorPage'))
const ResendVerificationPage = lazy(() => import('@/pages/auth/ResendVerificationPage'))
const OAuthCallbackPage = lazy(() => import('@/pages/auth/OAuthCallbackPage'))

const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'))
const OnboardingCompletePage = lazy(() => import('@/pages/onboarding/CompletePage'))

const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const GatewaysOverviewPage = lazy(() => import('@/pages/gateways/GatewaysOverviewPage'))

// Organization pages
const OrganizationListPage = lazy(() => import('@/pages/organization/OrganizationListPage'))
const OrganizationNewPage = lazy(() => import('@/pages/organization/OrganizationNewPage'))
const OrganizationDetailPage = lazy(() => import('@/pages/organization/OrganizationDetailPage'))
const OrganizationBillingPage = lazy(() => import('@/pages/organization/OrganizationBillingPage'))
const OrganizationTeamPage = lazy(() => import('@/pages/organization/OrganizationTeamPage'))
const OrganizationLlmKeysPage = lazy(() => import('@/pages/organization/OrganizationLlmKeysPage'))
const OrganizationCreditsPage = lazy(() => import('@/pages/organization/OrganizationCreditsPage'))
const OrganizationCostCentersPage = lazy(() => import('@/pages/organization/OrganizationCostCentersPage'))
const OrganizationTagsPage = lazy(() => import('@/pages/organization/OrganizationTagsPage'))
const CostAllocationPage = lazy(() => import('@/pages/organization/CostAllocationPage'))
const OrganizationDataRetentionPage = lazy(() => import('@/pages/organization/OrganizationDataRetentionPage'))
const OrganizationTeamsPage = lazy(() => import('@/pages/organization/OrganizationTeamsPage'))
const OrganizationManagementKeysPage = lazy(() => import('@/pages/organization/OrganizationManagementKeysPage'))
const TeamDetailPage = lazy(() => import('@/pages/organization/TeamDetailPage'))

// Workspace pages
const WorkspaceDashboardPage = lazy(() => import('@/pages/workspace/WorkspaceDashboardPage'))
const GatewayPage = lazy(() => import('@/pages/gateway/GatewayPage'))
const GatewayProvidersPage = lazy(() => import('@/pages/gateway/ProvidersPage'))
const GatewayModelsPage = lazy(() => import('@/pages/gateway/ModelsPage'))
const GatewayLogsPage = lazy(() => import('@/pages/gateway/LogsPage'))
const GatewayMonitoringPage = lazy(() => import('@/pages/gateway/MonitoringPage'))
const GatewayPerformancePage = lazy(() => import('@/pages/gateway/PerformancePage'))
const GatewayViolationsPage = lazy(() => import('@/pages/gateway/ViolationsPage'))
const GatewayConfigsPage = lazy(() => import('@/pages/gateway/ConfigsPage'))
const GatewayConfigDetailPage = lazy(() => import('@/pages/gateway/ConfigDetailPage'))

const ModelLabsPage = lazy(() => import('@/pages/lab/ModelLabsPage'))

const WorkspaceCostsPage = lazy(() => import('@/pages/workspace/costs/CostsPage'))
const WorkspaceCostsTokensPage = lazy(() => import('@/pages/workspace/costs/TokensPage'))
const WorkspaceCostsTrendsPage = lazy(() => import('@/pages/workspace/costs/TrendsPage'))
const WorkspaceCostsSavingsPage = lazy(() => import('@/pages/workspace/costs/SavingsPage'))
const WorkspaceCostsForecastPage = lazy(() => import('@/pages/workspace/costs/ForecastPage'))
const WorkspaceUsageAlertsPage = lazy(() => import('@/pages/workspace/costs/UsageAlertsPage'))
const WorkspaceBudgetsPage = lazy(() => import('@/pages/workspace/costs/BudgetsPage'))
const ProviderBillingPage = lazy(() => import('@/pages/workspace/costs/ProviderBillingPage'))
const WorkspaceCostBreakdownPage = lazy(() => import('@/pages/workspace/costs/CostBreakdownPage'))
const WorkspaceCostExportPage = lazy(() => import('@/pages/workspace/costs/CostExportPage'))

const WorkspacesPage = lazy(() => import('@/pages/workspace/WorkspacesPage'))
const WorkspaceSettingsMembersPage = lazy(() => import('@/pages/workspace/settings/MembersPage'))

// Admin pages
const AdminLoginPage = lazy(() => import('@/pages/admin/AdminLoginPage'))
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'))
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminOrgUsersPage'))
const AdminUserDetailPage = lazy(() => import('@/pages/admin/AdminUserDetailPage'))
const AdminOrganizationsPage = lazy(() => import('@/pages/admin/AdminOrganizationsPage'))
const AdminWorkspacesPage = lazy(() => import('@/pages/admin/AdminWorkspacesPage'))
const AdminModelsPage = lazy(() => import('@/pages/admin/AdminModelsPage'))
const AdminKeyPoolsPage = lazy(() => import('@/pages/admin/AdminKeyPoolsPage'))
const AdminKeyPoolDetailPage = lazy(() => import('@/pages/admin/AdminKeyPoolDetailPage'))
const AdminAdminApiKeysPage = lazy(() => import('@/pages/admin/AdminAdminApiKeysPage'))
const AdminGatewayKeysPage = lazy(() => import('@/pages/admin/AdminGatewayKeysPage'))
const AdminPlatformKeyRequestsPage = lazy(() => import('@/pages/admin/AdminPlatformKeyRequestsPage'))
const AdminUserLlmKeysPage = lazy(() => import('@/pages/admin/AdminUserLlmKeysPage'))
const AdminUserAdminKeysPage = lazy(() => import('@/pages/admin/AdminUserAdminKeysPage'))
const AdminAuditLogsPage = lazy(() => import('@/pages/admin/AdminAuditLogsPage'))
const AdminCreditsPage = lazy(() => import('@/pages/admin/AdminCreditsPage'))
const AdminAnalyticsCostsPage = lazy(() => import('@/pages/admin/AdminAnalyticsCostsPage'))
const AdminAnalyticsUsagePage = lazy(() => import('@/pages/admin/AdminAnalyticsUsagePage'))
const AdminAnalyticsErrorsPage = lazy(() => import('@/pages/admin/AdminAnalyticsErrorsPage'))
const AdminPlatformUsagePage = lazy(() => import('@/pages/admin/AdminPlatformUsagePage'))
const AdminDatabasePage = lazy(() => import('@/pages/admin/AdminDatabasePage'))
const AdminStatsPage = lazy(() => import('@/pages/admin/AdminStatsPage'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'))
const AdminCreditsTransactionsPage = lazy(() => import('@/pages/admin/AdminCreditsTransactionsPage'))
const AdminAdminsPage = lazy(() => import('@/pages/admin/AdminAdminsPage'))
const AdminTeamsPage = lazy(() => import('@/pages/admin/AdminTeamsPage'))
const AdminGatewayConfigsPage = lazy(() => import('@/pages/admin/AdminGatewayConfigsPage'))

// Wrapper for lazy loaded components
const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoading />}>{children}</Suspense>
)

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Index redirect
      { index: true, element: <Navigate to="/signin" replace /> },

      // Public legal routes
      {
        element: <LazyPage><LegalLayout /></LazyPage>,
        children: [
          { path: 'terms', element: <LazyPage><TermsPage /></LazyPage> },
          { path: 'privacy', element: <LazyPage><PrivacyPage /></LazyPage> },
        ],
      },

      // Public auth routes
      { path: 'signin', element: <LazyPage><SigninPage /></LazyPage> },
      { path: 'signup', element: <LazyPage><SignupPage /></LazyPage> },
      { path: 'login', element: <Navigate to="/signin" replace /> },

      // Auth callback routes
      {
        path: 'auth',
        children: [
          { path: 'forgot-password', element: <LazyPage><ForgotPasswordPage /></LazyPage> },
          { path: 'reset-password', element: <LazyPage><ResetPasswordPage /></LazyPage> },
          { path: 'verify-email', element: <LazyPage><VerifyEmailPage /></LazyPage> },
          { path: 'email-confirmed', element: <LazyPage><EmailConfirmedPage /></LazyPage> },
          { path: 'auth-code-error', element: <LazyPage><AuthCodeErrorPage /></LazyPage> },
          { path: 'resend-verification', element: <LazyPage><ResendVerificationPage /></LazyPage> },
          { path: 'callback', element: <LazyPage><OAuthCallbackPage /></LazyPage> },
        ],
      },

      // Protected user routes
      {
        element: <AuthGuard />,
        children: [
          // Onboarding (no sidebar)
          { path: 'onboarding', element: <LazyPage><OnboardingPage /></LazyPage> },
          { path: 'onboarding/complete', element: <LazyPage><OnboardingCompletePage /></LazyPage> },

          // Routes with sidebar
          {
            element: <DashboardLayoutWrapper />,
            children: [
              // Dashboard
              { path: 'dashboard', element: <LazyPage><DashboardPage /></LazyPage> },

              // Profile & Settings
              { path: 'profile', element: <LazyPage><ProfilePage /></LazyPage> },
              { path: 'settings', element: <LazyPage><SettingsPage /></LazyPage> },

              // Notifications
              { path: 'notifications', element: <LazyPage><NotificationsPage /></LazyPage> },

              // Gateways overview (cross-workspace)
              { path: 'gateways', element: <LazyPage><GatewaysOverviewPage /></LazyPage> },

              // Organization routes
              {
                path: 'organization',
                children: [
                  { index: true, element: <LazyPage><OrganizationListPage /></LazyPage> },
                  { path: 'new', element: <LazyPage><OrganizationNewPage /></LazyPage> },
                  { path: 'billing', element: <LazyPage><OrganizationBillingPage /></LazyPage> },
                  {
                    path: ':orgId',
                    children: [
                      { index: true, element: <LazyPage><OrganizationDetailPage /></LazyPage> },
                      { path: 'overview', element: <LazyPage><OrganizationDetailPage /></LazyPage> },
                      { path: 'users', element: <LazyPage><OrganizationTeamPage /></LazyPage> },
                      { path: 'cost-allocation', element: <LazyPage><CostAllocationPage /></LazyPage> },
                      // FO-UI4: old tag/cost-center pages now redirect into the unified page.
                      { path: 'settings/tags', element: <LazyPage><OrganizationTagsPage /></LazyPage> },
                      { path: 'settings/data-retention', element: <LazyPage><OrganizationDataRetentionPage /></LazyPage> },
                      { path: 'llm-keys', element: <LazyPage><OrganizationLlmKeysPage /></LazyPage> },
                      { path: 'management-keys', element: <LazyPage><OrganizationManagementKeysPage /></LazyPage> },
                      { path: 'credits', element: <LazyPage><OrganizationCreditsPage /></LazyPage> },
                      { path: 'credits/features', element: <LazyPage><OrganizationCreditsPage /></LazyPage> },
                      { path: 'credits/usage', element: <LazyPage><OrganizationCreditsPage /></LazyPage> },
                      { path: 'credits/transactions', element: <LazyPage><OrganizationCreditsPage /></LazyPage> },
                      { path: 'cost-centers', element: <LazyPage><OrganizationCostCentersPage /></LazyPage> },
                      { path: 'workspaces', element: <LazyPage><OrganizationDetailPage /></LazyPage> },
                      { path: 'teams', element: <LazyPage><OrganizationTeamsPage /></LazyPage> },
                      { path: 'teams/:teamId', element: <LazyPage><TeamDetailPage /></LazyPage> },
                    ],
                  },
                ],
              },

              // Gateway routes (workspace from context)
              {
                path: 'gateway',
                children: [
                  { index: true, element: <LazyPage><GatewayPage /></LazyPage> },
                  { path: 'providers', element: <LazyPage><GatewayProvidersPage /></LazyPage> },
                  { path: 'models', element: <LazyPage><GatewayModelsPage /></LazyPage> },
                  { path: 'logs', element: <LazyPage><GatewayLogsPage /></LazyPage> },
                  // FO-UI15: Gateway > Alerts renders the consistent alerts UI
                  // (UsageAlertsPage, shared with Cost > Budget & Alerts). The
                  // legacy gateway AlertsPage was removed — same backend,
                  // inconsistent UI.
                  { path: 'alerts', element: <LazyPage><WorkspaceUsageAlertsPage /></LazyPage> },
                  { path: 'monitoring', element: <LazyPage><GatewayMonitoringPage /></LazyPage> },
                  { path: 'performance', element: <LazyPage><GatewayPerformancePage /></LazyPage> },
                  { path: 'violations', element: <LazyPage><GatewayViolationsPage /></LazyPage> },
                ],
              },

              // Workspace Cost routes (workspace from context)
              {
                path: 'WorkspaceCost',
                children: [
                  { path: 'cost', element: <LazyPage><WorkspaceCostsPage /></LazyPage> },
                  { path: 'tokens', element: <LazyPage><WorkspaceCostsTokensPage /></LazyPage> },
                  { path: 'trends', element: <LazyPage><WorkspaceCostsTrendsPage /></LazyPage> },
                  { path: 'breakdown', element: <LazyPage><WorkspaceCostBreakdownPage /></LazyPage> },
                  { path: 'export', element: <LazyPage><WorkspaceCostExportPage /></LazyPage> },
                  { path: 'savings', element: <LazyPage><WorkspaceCostsSavingsPage /></LazyPage> },
                  { path: 'forecast', element: <LazyPage><WorkspaceCostsForecastPage /></LazyPage> },
                  // FO-UI15: Cost > Budget shows budget only (alerts moved to
                  // Gateway > Alerts). 'budget-alerts' kept as a backward-compat
                  // alias rendering the Budget page.
                  { path: 'budget-alerts', element: <LazyPage><WorkspaceBudgetsPage /></LazyPage> },
                  { path: 'budgets', element: <LazyPage><WorkspaceBudgetsPage /></LazyPage> },
                  { path: 'budget', element: <LazyPage><WorkspaceBudgetsPage /></LazyPage> },
                ],
              },

              // Cost routes (organization-level)
              {
                path: 'costs',
                children: [
                  { path: 'provider-billing', element: <LazyPage><ProviderBillingPage /></LazyPage> },
                ],
              },

              // Lab routes (workspace from context)
              {
                path: 'lab',
                children: [
                  { index: true, element: <Navigate to="/lab/chat" replace /> },
                  { path: 'chat', element: <LazyPage><ModelLabsPage /></LazyPage> },
                  { path: 'image', element: <LazyPage><ModelLabsPage /></LazyPage> },
                  { path: 'speech-to-text', element: <LazyPage><ModelLabsPage /></LazyPage> },
                  { path: 'translation', element: <LazyPage><ModelLabsPage /></LazyPage> },
                  { path: 'vision', element: <Navigate to="/lab/chat" replace /> },
                  { path: 'audio/speech-to-text', element: <Navigate to="/lab/speech-to-text" replace /> },
                  { path: 'audio/translation', element: <Navigate to="/lab/translation" replace /> },
                  { path: 'audio', element: <Navigate to="/lab/chat" replace /> },
                  { path: 'audio/text-to-speech', element: <Navigate to="/lab/chat" replace /> },
                  { path: 'audio/realtime', element: <Navigate to="/lab/chat" replace /> },
                  { path: 'audio/voice-cloning', element: <Navigate to="/lab/chat" replace /> },
                  { path: 'audio/audio-analysis', element: <Navigate to="/lab/chat" replace /> },
                  { path: 'docs', element: <Navigate to="/lab/chat" replace /> },
                ],
              },

              // Workspace routes (without ID - shows list/settings)
              { path: 'workspace', element: <LazyPage><WorkspacesPage /></LazyPage> },

              // Workspace routes (with ID)
              {
                path: 'workspace/:workspaceId',
                children: [
                  { index: true, element: <LazyPage><WorkspaceDashboardPage /></LazyPage> },

                  // Gateway config routes
                  {
                    path: 'gateway/configs',
                    children: [
                      { index: true, element: <LazyPage><GatewayConfigsPage /></LazyPage> },
                      { path: ':configId', element: <LazyPage><GatewayConfigDetailPage /></LazyPage> },
                    ],
                  },

                  // Documentation (top level)
                  { path: 'docs', element: <Navigate to="/lab/chat" replace /> },

                  // Costs
                  {
                    path: 'costs',
                    children: [
                      { index: true, element: <LazyPage><WorkspaceCostsPage /></LazyPage> },
                      { path: 'tokens', element: <LazyPage><WorkspaceCostsTokensPage /></LazyPage> },
                      { path: 'trends', element: <LazyPage><WorkspaceCostsTrendsPage /></LazyPage> },
                      { path: 'savings', element: <LazyPage><WorkspaceCostsSavingsPage /></LazyPage> },
                      { path: 'forecast', element: <LazyPage><WorkspaceCostsForecastPage /></LazyPage> },
                      { path: 'budget', element: <LazyPage><WorkspaceBudgetsPage /></LazyPage> },
                    ],
                  },

                  // LLM Costs
                  {
                    path: 'llm-costs',
                    children: [
                      { path: 'provider-costs', element: <LazyPage><WorkspaceCostsPage /></LazyPage> },
                      { path: 'budget', element: <LazyPage><WorkspaceBudgetsPage /></LazyPage> },
                    ],
                  },

                  // Settings
                  {
                    path: 'settings',
                    children: [
                      { index: true, element: <LazyPage><WorkspacesPage /></LazyPage> },
                      { path: 'members', element: <LazyPage><WorkspaceSettingsMembersPage /></LazyPage> },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // Admin routes
      {
        path: 'admin',
        children: [
          { path: 'login', element: <LazyPage><AdminLoginPage /></LazyPage> },
          {
            element: <AdminGuard />,
            children: [
              { index: true, element: <LazyPage><AdminDashboardPage /></LazyPage> },
              { path: 'users', element: <LazyPage><AdminUsersPage /></LazyPage> },
              { path: 'users/:userId', element: <LazyPage><AdminUserDetailPage /></LazyPage> },
              { path: 'organizations', element: <LazyPage><AdminOrganizationsPage /></LazyPage> },
              { path: 'workspaces', element: <LazyPage><AdminWorkspacesPage /></LazyPage> },
              { path: 'teams', element: <LazyPage><AdminTeamsPage /></LazyPage> },
              { path: 'gateway-configs', element: <LazyPage><AdminGatewayConfigsPage /></LazyPage> },
              { path: 'models', element: <LazyPage><AdminModelsPage /></LazyPage> },
              { path: 'key-pools', element: <LazyPage><AdminKeyPoolsPage /></LazyPage> },
              { path: 'key-pools/:poolId', element: <LazyPage><AdminKeyPoolDetailPage /></LazyPage> },
              { path: 'admin-api-keys', element: <LazyPage><AdminAdminApiKeysPage /></LazyPage> },
              { path: 'gateway-keys', element: <LazyPage><AdminGatewayKeysPage /></LazyPage> },
              { path: 'platform-key-requests', element: <LazyPage><AdminPlatformKeyRequestsPage /></LazyPage> },
              { path: 'user-llm-keys', element: <LazyPage><AdminUserLlmKeysPage /></LazyPage> },
              { path: 'user-admin-keys', element: <LazyPage><AdminUserAdminKeysPage /></LazyPage> },
              { path: 'audit-logs', element: <LazyPage><AdminAuditLogsPage /></LazyPage> },
              { path: 'credits', element: <LazyPage><AdminCreditsPage /></LazyPage> },
              { path: 'credits/transactions', element: <LazyPage><AdminCreditsTransactionsPage /></LazyPage> },
              { path: 'analytics/costs', element: <LazyPage><AdminAnalyticsCostsPage /></LazyPage> },
              { path: 'analytics/usage', element: <LazyPage><AdminAnalyticsUsagePage /></LazyPage> },
              { path: 'analytics/errors', element: <LazyPage><AdminAnalyticsErrorsPage /></LazyPage> },
              { path: 'platform-usage', element: <LazyPage><AdminPlatformUsagePage /></LazyPage> },
              { path: 'database', element: <LazyPage><AdminDatabasePage /></LazyPage> },
              { path: 'stats', element: <LazyPage><AdminStatsPage /></LazyPage> },
              { path: 'settings', element: <LazyPage><AdminSettingsPage /></LazyPage> },
              { path: 'admins', element: <LazyPage><AdminAdminsPage /></LazyPage> },
            ],
          },
        ],
      },
    ],
  },
])
